import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import {
	buildResolvedSmartLinkState,
	partitionResolvedLinks,
} from '@confluence/content-smartlinks/entry-points/buildResolvedSmartLinkState.ts';
import { fetchORSForSSR } from '@confluence/content-smartlinks/entry-points/fetchORSForSSR.ts';
import { getInitialState } from '@confluence/content-smartlinks/entry-points/getInitialState.ts';
import { getSmartShortcuts } from '@confluence/content-smartlinks/entry-points/getSmartShortcuts.ts';
import { expVal } from '@confluence/feature-experiments';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { timeGuard } from '@confluence/query-preloader-tools/entry-points/timeGuard.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getQueryBoundary } from '@confluence/query-preloader-tools/entry-points/queryBoundaryStorage.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import useShortcutsSpaceNavigationQuery, {
	type useShortcutsSpaceNavigationQuery$data,
	type useShortcutsSpaceNavigationQuery$variables,
} from './__generated__/useShortcutsSpaceNavigationQuery.graphql.ts';
import { shortcutsSmartLinksQuery } from './shortcutsSmartLinksQuery.ts';
import type {
	shortcutsSmartLinksQuery$data,
	shortcutsSmartLinksQuery$variables,
} from './__generated__/shortcutsSmartLinksQuery.graphql.ts';

declare global {
	interface Window {
		__SSR_SHORTCUTS__?: { [key: string]: any } | {};
	}
}

const SMART_LINK_MAX_TIME = 250;

const getSidenavSmartLinksORSMeasureName = () => {
	const queryBoundary = getQueryBoundary();

	if (!queryBoundary || queryBoundary === 'default') {
		return 'ssr-app/prepare/preloadQuery/fetch:sidenavSmartLinksORS';
	}

	return `ssr-app/prepare/preloadQuery/dataFetcher:${queryBoundary}/fetch:sidenavSmartLinksORS`;
};

/**
 * Shared ORS fetch logic: resolves a list of smart links via ORS and returns
 * their SmartCardProvider initial state. Used by both the legacy and experiment paths.
 */
const fetchORSInitialState = async (
	links: any[],
	objectResolverURL: string,
	userContext: any,
): Promise<Record<string, any>> => {
	let orsInitialState: Record<string, any> = {};

	await SSRMeasures.run(getSidenavSmartLinksORSMeasureName(), async () => {
		const timeout = StatsigConfigurations.getNumber(
			'confluence_ssr_sidenav_smart_link_max_time',
			SMART_LINK_MAX_TIME,
		);

		const resolvedSmartLinks = await timeGuard(
			async () => {
				try {
					const res = await fetchORSForSSR({
						url: `${objectResolverURL}?timeout=${timeout}`,
						userContext,
						body: JSON.stringify(links),
					});
					if (!res.ok) {
						throw new Error(`${res.status} ${res.statusText}`);
					}
					return res.json();
				} catch (e) {
					ssrPrepareErrorsClient.submitError(
						new Error(`Smart link shortcuts ORS call failed: ${e.message}`),
					);
				}
				return [];
			},
			[],
			timeout,
		);

		if (resolvedSmartLinks.length) {
			orsInitialState = getInitialState(resolvedSmartLinks);
		}
	});

	return orsInitialState;
};

export const preloadShortcutsSmartLinks = async (spaceKey: string): Promise<void> => {
	const { customURLs, userContext, cloudId } = getSSRRenderInputs();
	const objectResolverURL = customURLs?.['object-resolver-service'];

	if (expVal('confluence_skip_ors_shortcut_smartlinks', 'isEnabled', false)) {
		// Experiment ON: use the new confluence_shortcutSmartLinksBySpaceKey query which returns
		// enriched metadata for Confluence links, skipping ORS for those.
		// Non-Confluence links (Jira, external) still fall back to ORS.
		if (!cloudId) {
			return;
		}

		const res = await query<shortcutsSmartLinksQuery$data, shortcutsSmartLinksQuery$variables>({
			query: shortcutsSmartLinksQuery,
			variables: { spaceKey, cloudId },
		});

		// Map GQL nodes to SmartLinkType — the common shape used by partitionResolvedLinks
		// and buildResolvedSmartLinkState, keeping logic uniform with preloadContentSmartLinks.
		// Note: resolved and canView are always the same value from the BE.
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		const allLinks = (res?.data?.confluence_shortcutSmartLinksBySpaceKey?.nodes ?? [])
			.filter(
				(node): node is NonNullable<typeof node> & { url: string } =>
					node !== null && node !== undefined && Boolean(node.url),
			)
			.map((node) => ({
				appearance: 'inline' as const,
				resourceUrl: node.url,
				title: node.customTitle || node.title || null,
				emojiTitle: node.emojiTitle ?? null,
				canView: node.canView ?? null,
				product: node.product ?? null,
				resourceType: node.resourceType ?? null,
				subType: node.subType ?? null,
				iconUrl: node.iconUrl ?? null,
				resolved: node.resolved ?? null,
			}));

		if (!allLinks.length) {
			return;
		}

		// Partition: Confluence-resolved links skip ORS; unresolved (Jira, external) still need ORS.
		// Mirrors the same partitioning used in preloadContentSmartLinksWithSuspense.
		const { resolvedFromGql, linksForORS } = partitionResolvedLinks(allLinks);

		const confluenceInitialState =
			resolvedFromGql.length > 0 ? buildResolvedSmartLinkState(resolvedFromGql) : {};

		let orsInitialState: Record<string, any> = {};
		if (linksForORS.length > 0) {
			if (objectResolverURL) {
				// ORS only accepts { resourceUrl, appearance } — strip extra SmartLinkType fields
				// to avoid 400 errors. The legacy path uses getSmartShortcuts() which already
				// returns only these two fields, so sanitization is only needed here.
				const sanitizedLinksForORS = linksForORS.map(({ resourceUrl, appearance }) => ({
					resourceUrl,
					appearance,
				}));
				orsInitialState = await fetchORSInitialState(
					sanitizedLinksForORS,
					objectResolverURL,
					userContext,
				);
			} else {
				ssrPrepareErrorsClient.submitError(
					new Error(
						`Smart link shortcuts: ${linksForORS.length} unresolved link(s) could not be sent to ORS — object-resolver-service URL is missing from SSR render inputs.`,
					),
				);
			}
		}

		const initialState = { ...confluenceInitialState, ...orsInitialState };
		if (Object.keys(initialState).length > 0) {
			window['__SSR_SHORTCUTS__'] = initialState;
		}

		window['__SSR_SHORTCUTS_STATS__'] = {
			gql: {
				totalLinks: allLinks.length,
				resolvedFromGqlCount: resolvedFromGql.length,
				linksForORSCount: linksForORS.length,
				skippedORS: linksForORS.length === 0,
			},
		};
	} else {
		// Experiment OFF (legacy): use spaceSidebarLinks.quick + ORS for all shortcuts.
		if (!objectResolverURL) {
			return;
		}

		const res =
			!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_core_frag_reader') &&
			expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
				? {
						data: readQueryInterop<
							useShortcutsSpaceNavigationQuery$data,
							useShortcutsSpaceNavigationQuery$variables
						>({
							query: useShortcutsSpaceNavigationQuery,
							variables: { spaceKey },
						}),
					}
				: await query<
						useShortcutsSpaceNavigationQuery$data,
						useShortcutsSpaceNavigationQuery$variables
					>({
						query: useShortcutsSpaceNavigationQuery,
						variables: { spaceKey },
					});

		const smartLinks = getSmartShortcuts(res);
		if (!smartLinks?.length) {
			return;
		}

		const initialState = await fetchORSInitialState(smartLinks, objectResolverURL, userContext);
		if (Object.keys(initialState).length > 0) {
			// Format response to fit SmartCard Provider storeOptions initial state
			// Save correctly formatted response to window obj so it can be used on SSR and SPA (no duplicate calls)
			window['__SSR_SHORTCUTS__'] = initialState;
		}

		window['__SSR_SHORTCUTS_STATS__'] = {
			gql: {
				totalLinks: smartLinks.length,
				resolvedFromGqlCount: 0,
				linksForORSCount: smartLinks.length,
				skippedORS: false,
			},
		};
	}
};
